/* eslint-disable no-underscore-dangle */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  confirmCertCodeAPI,
  sendVerificationAPI,
  checkInfoAPI,
} from '../api/auth';
import { loadProfileAPI, saveProfileAPI } from '../api/profile';
import { getUserPId } from '../common/util';

const MODULE_PROFILE = 'profile';
const initialState = {
  pending: false,
  user: {
    userId: '',
    lastName: '',
    firstName: '',
    nickName: '',
    email: '',
    tel: '',
    institution: '',
    verified: { tel: false, email: false },
  },
  checking: { email: false, tel: false, auth: false, send: false },
  checked: { email: null, tel: null, auth: null, send: null },
  error: { email: null, tel: null, auth: null, send: null },
  saved: null,
};

export const loadProfile = createAsyncThunk(
  `${MODULE_PROFILE}/LOAD`,
  async (_undefined, { rejectWithValue }) => {
    try {
      const { result, data } = await loadProfileAPI(getUserPId());
      if (result) {
        return data;
      }
      return rejectWithValue('회원 정보 로드에 실패하였습니다.');
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const saveProfile = createAsyncThunk(
  `${MODULE_PROFILE}/SAVE`,
  async (data, { rejectWithValue }) => {
    try {
      const { result } = await saveProfileAPI({
        ...data,
        userPId: getUserPId(),
      });
      if (result) return true;
      return rejectWithValue('회원 정보 수정에 실패하였습니다.');
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const checkInfo = createAsyncThunk(
  `${MODULE_PROFILE}/CHECK_INFO`,
  async ({ type, value }, { rejectWithValue }) => {
    try {
      const { result } = await checkInfoAPI({ type, value });
      if (result) {
        return { [type]: result };
      }
      return rejectWithValue(
        `이미 등록된 ${type === 'email' ? '이메일' : '전화번호'}입니다.`,
      );
    } catch (err) {
      return rejectWithValue(
        '알 수 없는 오류가 발생했습니다. 관리자에게 문의하세요.',
      );
    }
  },
);

export const confirmCode = createAsyncThunk(
  `${MODULE_PROFILE}/CONFIRM_CODE`,
  async ({ code, value }, { rejectWithValue }) => {
    try {
      const { result } = await confirmCertCodeAPI({
        code,
        type: 'updateinfo',
      });
      if (result) {
        const { result: r } = await saveProfileAPI({
          ...value,
          userPId: getUserPId(),
        });
        return r;
      }
      return rejectWithValue(false);
    } catch (err) {
      return rejectWithValue(false);
    }
  },
);

export const sendVerifyMsg = createAsyncThunk(
  `${MODULE_PROFILE}/SEND_MESSAGE`,
  async (data, { rejectWithValue }) => {
    try {
      const { result, error } = await sendVerificationAPI({
        ...data,
        type: 'updateinfo',
      });
      if (result) {
        return result;
      }

      if (error) {
        const { code, desc } = error;
        if (code === '4v102a') {
          return rejectWithValue(
            '인증번호 전송에 필요한 정보가 부족합니다. 다시 시도해주세요.',
          );
        }
        if (code === '4v102b') {
          return rejectWithValue(
            '유효한 인증정보가 아닙니다. 다른 정보로 시도해주세요.',
          );
        }
        return rejectWithValue(desc);
      }
      return rejectWithValue(
        '인증정보 전송 중 오류가 발생하였습니다. 다시 시도해주세요.',
      );
    } catch (err) {
      return rejectWithValue(
        '인증정보 전송 중 오류가 발생하였습니다. 다시 시도해주세요.',
      );
    }
  },
);

const profileSlice = createSlice({
  name: MODULE_PROFILE,
  initialState,
  reducers: {},
  extraReducers: {
    [loadProfile.pending.type]: () => ({
      ...initialState,
      pending: true,
    }),
    [loadProfile.fulfilled.type]: (state, action) => ({
      ...state,
      pending: false,
      user: action.payload,
    }),
    [loadProfile.rejected.type]: (state, action) => ({
      ...state,
      pending: false,
      error: action.payload,
    }),
    [saveProfile.pending.type]: (state) => ({
      ...state,
      pending: true,
      saved: null,
    }),
    [saveProfile.fulfilled.type]: (state) => ({
      ...state,
      pending: false,
      saved: true,
    }),
    [saveProfile.rejected.type]: (state, action) => ({
      ...state,
      pending: false,
      error: action.payload,
      saved: false,
    }),
    [checkInfo.pending.type]: (state, action) => ({
      ...state,
      checking: { ...state.checking, [action.meta.arg.type]: true },
      checked: { ...state.checked, [action.meta.arg.type]: null },
      error: { ...state.error, [action.meta.arg.type]: null },
    }),
    [checkInfo.fulfilled.type]: (state, action) => ({
      ...state,
      checking: { ...state.checking, email: false, tel: false },
      checked: { ...state.checked, ...action.payload },
    }),
    [checkInfo.rejected.type]: (state, action) => ({
      ...state,
      checking: { ...state.checking, [action.meta.arg.type]: false },
      checked: { ...state.checked, [action.meta.arg.type]: false },
      error: { ...state.error, [action.meta.arg.type]: action.payload },
    }),
    [confirmCode.pending.type]: (state) => ({
      ...state,
      checking: { ...state.checking, auth: true },
      checked: { ...state.checked, auth: null },
      error: { ...state.error, auth: null },
    }),
    [confirmCode.fulfilled.type]: (state, action) => ({
      ...state,
      checking: { ...state.checking, auth: false },
      checked: { ...state.checked, auth: action.payload },
    }),
    [confirmCode.rejected.type]: (state, action) => ({
      ...state,
      checking: { ...state.checking, auth: false },
      checked: { ...state.checked, auth: false },
      error: { ...state.error, auth: action.payload },
    }),
    [sendVerifyMsg.pending.type]: (state) => ({
      ...state,
      checking: { ...state.checking, send: true },
      checked: { ...state.checked, send: false },
      error: { ...state.error, send: null },
    }),
    [sendVerifyMsg.fulfilled.type]: (state, action) => ({
      ...state,
      checking: { ...state.checking, send: false },
      checked: { ...state.checked, send: action.payload },
    }),
    [sendVerifyMsg.rejected.type]: (state, action) => ({
      ...state,
      checking: { ...state.checking, send: false },
      checked: { ...state.checked, send: null },
      error: { ...state.error, send: action.payload },
    }),
  },
});

const { reducer: ProfileReducer } = profileSlice;

export default ProfileReducer;
